import { useState, useEffect, useRef } from "react";
import { LANES, DRIVERS, STATUSES, S, COLOR_PALETTE, SCORE_STYLE, BUSINESS_AREAS, CONFIDENCE_LEVELS, INITIATIVE_STAGES, PRIMARY_SURFACES } from "./constants";
import { supabase } from "./supabaseClient";

// ─── DriverTag ────────────────────────────────────────────────────────────────
export function DriverTag({ driver, compact = false }) {
  const d = DRIVERS[driver] || DRIVERS.core;
  return (
    <span style={{
      display:"inline-flex", alignItems:"center", gap:3,
      fontSize: compact ? 8 : 10, fontWeight:700,
      padding: compact ? "1px 5px" : "2px 7px",
      borderRadius:4, background:d.bg, color:d.color, border:`1px solid ${d.border}`,
      letterSpacing:"0.02em", whiteSpace:"nowrap", flexShrink:0, lineHeight:1.5,
    }}>
      <span style={{ width:5, height:5, borderRadius:"50%", background:d.color, display:"inline-block", flexShrink:0 }} />
      {compact ? d.short : d.label}
    </span>
  );
}

// ─── StatusBadge ──────────────────────────────────────────────────────────────
export function StatusBadge({ status }) {
  const s = STATUSES[status] || STATUSES.backlog;
  return (
    <span style={{
      fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:12,
      background:s.bg, color:s.color, border:`1px solid ${s.border}`,
      whiteSpace:"nowrap",
    }}>{s.label}</span>
  );
}

// ─── ViewToggle ───────────────────────────────────────────────────────────────
export function ViewToggle({ view, onChange }) {
  return (
    <div style={{ display:"flex", background:"var(--bg-hover)", borderRadius:9, padding:3, gap:2 }}>
      {[{ id:"timeline", icon:"▦", label:"Timeline" }, { id:"executive", icon:"▤", label:"Executive" }, { id:"list", icon:"☰", label:"List" }, { id:"digest", icon:"◫", label:"Digest" }, { id:"release", icon:"◷", label:"Release" }, { id:"jira", icon:"◈", label:"Jira" }, { id:"dashboard", icon:"◉", label:"Dashboard" }].map((v) => (
        <button key={v.id} onClick={() => onChange(v.id)} style={{
          display:"flex", alignItems:"center", gap:5,
          padding:"5px 12px", borderRadius:7, border:"none", cursor:"pointer",
          fontSize:12, fontWeight:700, fontFamily:"inherit", letterSpacing:"-0.01em",
          background: view===v.id ? "var(--bg-card)" : "transparent",
          color:      view===v.id ? "var(--accent)" : "var(--text-secondary)",
          boxShadow:  view===v.id ? "0 1px 3px rgba(0,0,0,0.10), 0 1px 2px rgba(0,0,0,0.06)" : "none",
          transition: "all 0.15s",
        }}>
          <span style={{ fontSize:14 }}>{v.icon}</span>{v.label}
        </button>
      ))}
    </div>
  );
}

// ─── ConfluenceIcon ───────────────────────────────────────────────────────────
export function ConfluenceIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
      <path d="M1.26 17.71c-.33.53-.7 1.14-.97 1.57a.96.96 0 0 0 .33 1.32l3.3 1.96a.96.96 0 0 0 1.32-.34c.25-.4.6-.98.97-1.58 2.6-4.18 5.22-3.67 9.93-1.33l3.3 1.7a.96.96 0 0 0 1.28-.43l1.7-3.4a.96.96 0 0 0-.43-1.28c-.9-.45-2.7-1.37-4.3-2.19-7.62-3.86-13.73-3.6-16.43 1.99z" fill="var(--accent)"/>
      <path d="M22.74 6.29c.33-.53.7-1.14.97-1.57a.96.96 0 0 0-.33-1.32L20.08 1.44a.96.96 0 0 0-1.32.34c-.25.4-.6.98-.97 1.58-2.6 4.18-5.22 3.67-9.93 1.33l-3.3-1.7a.96.96 0 0 0-1.28.43l-1.7 3.4a.96.96 0 0 0 .43 1.28c.9.45 2.7 1.37 4.3 2.19 7.62 3.86 13.73 3.6 16.43-1.99z" fill="var(--accent)"/>
    </svg>
  );
}

// ─── DeleteButton ─────────────────────────────────────────────────────────────
export function DeleteButton({ onConfirm, compact = false, ariaLabel = "Delete" }) {
  const [confirming, setConfirming] = useState(false);

  if (confirming) {
    return (
      <span style={{ display:"inline-flex", alignItems:"center", gap:3, flexShrink:0 }}>
        <span style={{ fontSize: compact ? 9 : 10, fontWeight:700, color:"#EF4444", whiteSpace:"nowrap" }}>Sure?</span>
        <button
          onClick={(e) => { e.stopPropagation(); onConfirm(); }}
          style={{ background:"var(--color-danger-bg,#FEE2E2)", border:"1px solid var(--color-danger-border,#FCA5A5)", borderRadius:3, color:"var(--color-danger,#DC2626)",
            cursor:"pointer", fontSize: compact ? 9 : 10, fontWeight:800, padding:"1px 5px",
            fontFamily:"inherit", lineHeight:1.4 }}>
          Yes
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); setConfirming(false); }}
          style={{ background:"var(--bg-subtle)", border:"1px solid var(--border)", borderRadius:3, color:"var(--text-secondary)",
            cursor:"pointer", fontSize: compact ? 9 : 10, fontWeight:700, padding:"1px 5px",
            fontFamily:"inherit", lineHeight:1.4 }}>
          No
        </button>
      </span>
    );
  }

  return (
    <button
      onClick={(e) => { e.stopPropagation(); setConfirming(true); }}
      title="Delete" aria-label={ariaLabel}
      style={{ background:"transparent", border:"none", cursor:"pointer",
        fontSize: compact ? 12 : 14, padding: compact ? 0 : "2px 4px",
        color:"var(--text-muted)", lineHeight:1, fontFamily:"inherit", borderRadius:3, flexShrink:0 }}
      onMouseEnter={(e) => e.currentTarget.style.color="var(--color-danger,#EF4444)"}
      onMouseLeave={(e) => e.currentTarget.style.color="var(--text-muted)"}>
      ×
    </button>
  );
}

// ─── InviteModal ──────────────────────────────────────────────────────────────
export function InviteModal({ onInvite, onClose }) {
  const [email,   setEmail]   = useState("");
  const [role,    setRole]    = useState("read");
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");
  const [sent,    setSent]    = useState(false);

  const ROLES = [
    { id:"super_admin", label:"Super Admin", desc:"Can invite others and edit the roadmap" },
    { id:"write",       label:"Admin",       desc:"Can edit the roadmap" },
    { id:"read",        label:"Read Only",   desc:"Can view the roadmap only" },
    { id:"marketing",   label:"Marketing",   desc:"Can edit the release calendar only" },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await onInvite(email, role);
      setSent(true);
    } catch (err) {
      setError(err.message || "Failed to send invite.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={S.overlay}>
      <div style={{ ...S.modal, width:400 }}>
        {sent ? (
          <div style={{ textAlign:"center", padding:"8px 0" }}>
            <div style={{ fontSize:28, marginBottom:10 }}>✉</div>
            <div style={{ fontSize:15, fontWeight:800, color:"var(--text-primary)", marginBottom:8 }}>Invite sent!</div>
            <div style={{ fontSize:12, color:"var(--text-secondary)", marginBottom:20 }}>
              {email} will receive an email to set their password.
            </div>
            <div style={{ display:"flex", gap:8, justifyContent:"center" }}>
              <button onClick={() => { setSent(false); setEmail(""); setRole("read"); }} style={S.btnPrimary}>
                Invite another
              </button>
              <button onClick={onClose} style={S.btnGhost}>Done</button>
            </div>
          </div>
        ) : (
          <>
            <div style={S.modalTitle}>Invite User</div>
            <form onSubmit={handleSubmit} style={{ display:"flex", flexDirection:"column", gap:14 }}>
              <div>
                <label htmlFor="invite-email" style={S.label}>Email</label>
                <input id="invite-email" type="email" value={email} onChange={e => { setEmail(e.target.value); setError(""); }}
                  style={S.input} autoFocus placeholder="colleague@company.com" />
              </div>
              <div>
                <label style={S.label}>Role</label>
                <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                  {ROLES.map(r => (
                    <div key={r.id} onClick={() => setRole(r.id)} style={{
                      display:"flex", alignItems:"center", gap:10,
                      padding:"10px 12px", borderRadius:7, cursor:"pointer",
                      border:`1.5px solid ${role===r.id ? "var(--accent)" : "var(--border)"}`,
                      background: role===r.id ? "var(--bg-hover)" : "var(--bg-subtle)",
                      transition:"all 0.12s",
                    }}>
                      <div style={{
                        width:16, height:16, borderRadius:"50%", flexShrink:0,
                        border:`2px solid ${role===r.id ? "var(--accent)" : "var(--border)"}`,
                        background: role===r.id ? "var(--accent)" : "var(--bg-input)",
                        display:"flex", alignItems:"center", justifyContent:"center",
                      }}>
                        {role===r.id && <span style={{ width:6, height:6, borderRadius:"50%", background:"var(--bg-card)", display:"block" }} />}
                      </div>
                      <div>
                        <div style={{ fontSize:12, fontWeight:700, color: role===r.id ? "var(--accent)" : "var(--text-primary)" }}>{r.label}</div>
                        <div style={{ fontSize:10, color:"var(--text-secondary)", marginTop:1 }}>{r.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {error && <div role="alert" style={{ fontSize:11, color:"#EF4444", fontWeight:700 }}>{error}</div>}
              <div style={{ display:"flex", gap:8 }}>
                <button type="submit" disabled={loading} style={{ ...S.btnPrimary, opacity: loading ? 0.7 : 1 }}>
                  {loading ? "Sending…" : "Send Invite"}
                </button>
                <button type="button" onClick={onClose} style={S.btnGhost}>Cancel</button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

// ─── RoadmapPicker ────────────────────────────────────────────────────────────
export function RoadmapPicker({ roadmaps, activeId, canEdit, onSwitch, onCreate, onDelete, onRename }) {
  const [open,        setOpen]       = useState(false);
  const [showCreate,  setShowCreate] = useState(false);
  const [newName,     setNewName]    = useState("");
  const [saving,      setSaving]     = useState(false);
  const [createError, setCreateError] = useState("");
  const [renamingId,  setRenamingId] = useState(null);
  const [renameVal,   setRenameVal]  = useState("");
  // Optimistic name cache — populated on create so the button shows the right
  // name immediately, before the DB refetch arrives
  const [nameCache, setNameCache] = useState({});

  const activeName = roadmaps.find(r => r.id === activeId)?.name
                  || nameCache[activeId]
                  || "Roadmap";

  const closeAll = () => { setOpen(false); setShowCreate(false); setRenamingId(null); setCreateError(""); };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setSaving(true); setCreateError("");
    try {
      const trimmed = newName.trim();
      const id = await onCreate(trimmed);
      setNameCache(c => ({ ...c, [id]: trimmed })); // show name immediately before DB refetch
      setNewName(""); setShowCreate(false);
      onSwitch(id); setOpen(false);
    } catch (err) {
      setCreateError(err.message || "Failed to create roadmap");
    } finally { setSaving(false); }
  };

  const [renameError, setRenameError] = useState("");

  const handleRename = async (e) => {
    e.preventDefault();
    if (!renameVal.trim()) return;
    setRenameError("");
    try {
      await onRename(renamingId, renameVal.trim());
      setRenamingId(null); setRenameVal("");
    } catch (err) { setRenameError(err.message || "Failed to rename"); }
  };

  const handleDelete = async (id) => {
    try {
      await onDelete(id);
      if (id === activeId) onSwitch("main");
    } catch (err) { console.error(err); }
  };

  return (
    <div style={{ position:"relative" }}>
      <button onClick={() => setOpen(v => !v)} style={{
        display:"flex", alignItems:"center", gap:5,
        padding:"5px 10px", borderRadius:6, border:"1px solid var(--border)",
        background: open ? "var(--bg-hover)" : "var(--bg-card)",
        fontSize:12, fontWeight:700, color:"var(--text-primary)",
        cursor:"pointer", fontFamily:"inherit", maxWidth:180,
      }}>
        <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1 }}>
          {activeName}
        </span>
        <span style={{ fontSize:9, color:"var(--text-muted)", flexShrink:0 }}>▾</span>
      </button>

      {open && (
        <>
          <div style={{ position:"fixed", inset:0, zIndex:49 }} onClick={closeAll} />
          <div style={{
            position:"absolute", top:"calc(100% + 6px)", left:0, zIndex:50,
            background:"var(--bg-card)", border:"1px solid var(--border)", borderRadius:8,
            boxShadow:"0 8px 24px rgba(0,0,0,0.12)", minWidth:230, overflow:"hidden",
          }}>
            <div style={{ padding:"8px 12px 4px", fontSize:9, fontWeight:800, color:"var(--text-muted)", letterSpacing:"0.08em" }}>
              ROADMAPS
            </div>

            {roadmaps.map(r => (
              <div key={r.id}>
                {renamingId === r.id ? (
                  <div style={{ padding:"4px 8px 4px 12px" }}>
                    <form onSubmit={handleRename} style={{ display:"flex", gap:4 }}>
                      <input value={renameVal} onChange={e => { setRenameVal(e.target.value); setRenameError(""); }}
                        style={{ ...S.input, flex:1, fontSize:11, padding:"3px 6px" }} autoFocus />
                      <button type="submit" style={{ ...S.btnPrimary, fontSize:10, padding:"3px 8px" }}>✓</button>
                      <button type="button" onClick={() => { setRenamingId(null); setRenameError(""); }}
                        style={{ ...S.btnGhost, fontSize:10, padding:"3px 8px" }}>✕</button>
                    </form>
                    {renameError && (
                      <div role="alert" style={{ fontSize:10, color:"#EF4444", fontWeight:700, marginTop:3 }}>{renameError}</div>
                    )}
                  </div>
                ) : (
                  <div style={{
                    display:"flex", alignItems:"center",
                    background: r.id === activeId ? "var(--bg-hover)" : "transparent",
                  }}
                  onMouseEnter={e => { if (r.id !== activeId) e.currentTarget.style.background="var(--bg-subtle)"; }}
                  onMouseLeave={e => { if (r.id !== activeId) e.currentTarget.style.background="transparent"; }}>
                    <button onClick={() => { onSwitch(r.id); setOpen(false); }} style={{
                      flex:1, textAlign:"left", background:"transparent", border:"none",
                      padding:"9px 4px 9px 12px", fontSize:12, cursor:"pointer", fontFamily:"inherit",
                      fontWeight: r.id === activeId ? 700 : 600,
                      color: r.id === activeId ? "var(--text-primary)" : "var(--text-secondary)",
                      overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                    }}>
                      {r.id === activeId ? "✓ " : "\u00A0\u00A0"}{r.name}
                    </button>
                    {canEdit && (
                      <div style={{ display:"flex", padding:"0 6px", flexShrink:0, gap:0 }}>
                        <button onClick={() => { setRenamingId(r.id); setRenameVal(r.name); setRenameError(""); }}
                          title="Rename" style={{
                            background:"transparent", border:"none", cursor:"pointer",
                            color:"var(--text-muted)", fontSize:11, padding:"2px 4px", borderRadius:3,
                          }}
                          onMouseEnter={e => e.currentTarget.style.color="var(--text-primary)"}
                          onMouseLeave={e => e.currentTarget.style.color="var(--text-muted)"}>✏</button>
                        {r.id !== "main" && (
                          <DeleteButton onConfirm={() => handleDelete(r.id)} compact />
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            {canEdit && (
              <>
                <div style={{ height:1, background:"var(--border)", margin:"4px 0" }} />
                {showCreate ? (
                  <div style={{ padding:"6px 8px" }}>
                    <form onSubmit={handleCreate} style={{ display:"flex", gap:4 }}>
                      <input value={newName} onChange={e => { setNewName(e.target.value); setCreateError(""); }}
                        placeholder="Roadmap name…"
                        style={{ ...S.input, flex:1, fontSize:11, padding:"4px 8px" }} autoFocus />
                      <button type="submit" disabled={saving}
                        style={{ ...S.btnPrimary, fontSize:10, padding:"4px 8px" }}>
                        {saving ? "…" : "Create"}
                      </button>
                      <button type="button" onClick={() => { setShowCreate(false); setNewName(""); setCreateError(""); }}
                        style={{ ...S.btnGhost, fontSize:10, padding:"4px 8px" }}>✕</button>
                    </form>
                    {createError && (
                      <div role="alert" style={{ fontSize:10, color:"#EF4444", fontWeight:700, marginTop:4, padding:"3px 4px",
                        background:"#FEF2F2", borderRadius:3, border:"1px solid #FECACA" }}>
                        {createError}
                      </div>
                    )}
                  </div>
                ) : (
                  <button onClick={() => setShowCreate(true)} style={{
                    display:"block", width:"100%", textAlign:"left",
                    padding:"9px 12px", fontSize:12, fontWeight:600, color:"var(--accent)",
                    background:"transparent", border:"none", cursor:"pointer", fontFamily:"inherit",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background="var(--bg-subtle)"}
                  onMouseLeave={e => e.currentTarget.style.background="transparent"}>
                    + New Roadmap
                  </button>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─── UserManagementModal ──────────────────────────────────────────────────────
export function UserManagementModal({ currentUserId, onManage, onClose, onInvite }) {
  const [users,     setUsers]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState("");
  const [saving,    setSaving]    = useState(null); // userId currently being saved
  const [resending, setResending] = useState(null); // userId currently resending invite
  const [toast,     setToast]     = useState("");   // success toast message

  const formatLastSeen = (ts) => {
    if (!ts) return null;
    const d = new Date(ts);
    const now = Date.now();
    const diff = now - d.getTime();
    if (diff < 60_000)  return "just now";
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
    if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d ago`;
    return d.toLocaleDateString("en-US", { month:"short", day:"numeric", year:"numeric" });
  };

  const ROLES = [
    { id:"super_admin", label:"Super Admin" },
    { id:"write",       label:"Admin" },
    { id:"read",        label:"Read Only" },
    { id:"marketing",   label:"Marketing" },
  ];

  const roleStyle = {
    super_admin: { background:"#1D4ED822", color:"#60A5FA", border:"1.5px solid #1D4ED844" },
    write:       { background:"#15803D22", color:"#4ADE80", border:"1.5px solid #15803D44" },
    read:        { background:"var(--bg-subtle)", color:"var(--text-secondary)", border:"1.5px solid var(--border)" },
    marketing:   { background:"#DB277722", color:"#F472B6", border:"1.5px solid #DB277744" },
  };

  useEffect(() => {
    (async () => {
      try {
        const result = await onManage("list");
        setUsers(result.users || []);
      } catch (err) {
        setError(err.message || "Failed to load users");
      } finally {
        setLoading(false);
      }
    })();
  }, [onManage]);

  const handleRoleChange = async (userId, newRole) => {
    setSaving(userId); setError("");
    try {
      await onManage("updateRole", userId, newRole);
      setUsers(u => u.map(x => x.id === userId ? { ...x, role: newRole } : x));
    } catch (err) {
      setError(err.message || "Failed to update role");
    } finally {
      setSaving(null);
    }
  };

  const handleDelete = async (userId) => {
    setSaving(userId); setError("");
    try {
      await onManage("deleteUser", userId);
      setUsers(u => u.filter(x => x.id !== userId));
    } catch (err) {
      setError(err.message || "Failed to delete user");
      setSaving(null);
    }
  };

  const handleResendInvite = async (userId, email) => {
    setResending(userId); setError("");
    try {
      await onManage("resendInvite", null, null, email);
      setToast(`Invite sent to ${email}`);
      setTimeout(() => setToast(""), 3000);
    } catch (err) {
      setError(err.message || "Failed to resend invite");
    } finally {
      setResending(null);
    }
  };

  return (
    <div style={S.overlay}>
      <div style={{ ...S.modal, width:520, maxHeight:"80vh", display:"flex", flexDirection:"column" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
          <div style={S.modalTitle}>Manage Users</div>
          <button onClick={onInvite} style={{ ...S.btnPrimary, fontSize:11, padding:"6px 12px" }}>
            + Invite User
          </button>
        </div>

        {error && (
          <div role="alert" style={{ fontSize:11, color:"#F87171", fontWeight:700, marginBottom:10, padding:"6px 10px",
            background:"#EF444422", borderRadius:5, border:"1px solid #EF444444" }}>
            {error}
          </div>
        )}

        {toast && (
          <div style={{ fontSize:11, color:"#4ADE80", fontWeight:700, marginBottom:10, padding:"6px 10px",
            background:"#15803D22", borderRadius:5, border:"1px solid #15803D44" }}>
            ✓ {toast}
          </div>
        )}

        <div style={{ flex:1, overflowY:"auto" }}>
          {loading ? (
            <div style={{ textAlign:"center", padding:"32px 0", color:"var(--text-muted)", fontSize:13 }}>
              Loading users…
            </div>
          ) : users.length === 0 ? (
            <div style={{ textAlign:"center", padding:"32px 0", color:"var(--text-muted)", fontSize:13 }}>
              No users found.
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
              {users.map(user => {
                const isSelf       = user.id === currentUserId;
                const isSaving     = saving === user.id;
                const isResending  = resending === user.id;
                const neverLoggedIn = !user.lastSignIn;
                const rs           = roleStyle[user.role] || roleStyle.read;
                const lastSeen     = formatLastSeen(user.lastSignIn);
                return (
                  <div key={user.id} style={{
                    display:"flex", alignItems:"center", gap:10,
                    padding:"10px 12px", borderRadius:7,
                    background: isSelf ? "var(--bg-hover)" : "var(--bg-subtle)",
                    border:"1px solid var(--border)",
                    opacity: isSaving ? 0.55 : 1,
                    transition:"opacity 0.15s",
                  }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:12, fontWeight:700, color:"var(--text-primary)",
                        overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {user.email}
                      </div>
                      <div style={{ fontSize:10, color:"var(--text-muted)", marginTop:1, display:"flex", alignItems:"center", gap:6 }}>
                        {isSelf && <span>you ·</span>}
                        {neverLoggedIn ? (
                          <span style={{ color:"#B45309", fontWeight:700 }}>Never logged in</span>
                        ) : (
                          <span>Last seen {lastSeen}</span>
                        )}
                      </div>
                    </div>
                    {neverLoggedIn && !isSelf && (
                      <button
                        onClick={() => handleResendInvite(user.id, user.email)}
                        disabled={!!resending || !!saving}
                        style={{
                          fontSize:10, fontWeight:700, padding:"3px 8px", borderRadius:4,
                          border:"1px solid #FCD34D", background:"#FFFBEB", color:"#92400E",
                          cursor: (!!resending || !!saving) ? "not-allowed" : "pointer",
                          fontFamily:"inherit", opacity: isResending ? 0.55 : 1, flexShrink:0,
                        }}
                      >
                        {isResending ? "Sending…" : "Resend"}
                      </button>
                    )}
                    <select
                      value={user.role}
                      disabled={isSelf || !!saving}
                      onChange={e => handleRoleChange(user.id, e.target.value)}
                      style={{
                        fontSize:11, fontWeight:700, padding:"4px 8px", borderRadius:5,
                        fontFamily:"inherit", appearance:"none",
                        cursor: isSelf ? "not-allowed" : "pointer",
                        opacity: isSelf ? 0.65 : 1,
                        ...rs,
                      }}
                      aria-label={`Role for ${user.email || "user"}`}
                    >
                      {ROLES.map(r => (
                        <option key={r.id} value={r.id}>{r.label}</option>
                      ))}
                    </select>
                    {isSelf ? (
                      <div style={{ width:22, flexShrink:0 }} />
                    ) : (
                      <DeleteButton onConfirm={() => handleDelete(user.id)} compact />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ marginTop:16, display:"flex", justifyContent:"flex-end" }}>
          <button onClick={onClose} style={S.btnGhost}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── LaneManagerModal helpers (module-level so they don't remount on rerender) ─
function lmPaletteFor(colorId) {
  return COLOR_PALETTE.find(c => c.id === colorId) || COLOR_PALETTE[0];
}

function LMColorPicker({ value, onChange }) {
  return (
    <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
      {COLOR_PALETTE.map(c => (
        <button key={c.id} type="button" onClick={() => onChange(c.id)} style={{
          width:20, height:20, borderRadius:"50%", background:c.color, cursor:"pointer", flexShrink:0,
          border: value === c.id ? `3px solid #0F172A` : "2px solid transparent",
          outline: value === c.id ? `2px solid ${c.color}` : "none", outlineOffset:1,
        }} />
      ))}
    </div>
  );
}

function LMCapacity({ value, onChange }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:4 }}>
      <button type="button" onClick={() => onChange(Math.max(1, value - 1))}
        style={{ ...S.btnGhost, padding:"1px 8px", fontSize:16, lineHeight:1.2 }}>−</button>
      <span style={{ fontSize:12, fontWeight:700, minWidth:18, textAlign:"center" }}>{value}</span>
      <button type="button" onClick={() => onChange(Math.min(6, value + 1))}
        style={{ ...S.btnGhost, padding:"1px 8px", fontSize:16, lineHeight:1.2 }}>+</button>
    </div>
  );
}

function LMInlineForm({ form, setForm, onSubmit, onCancel, submitLabel }) {
  const palette = lmPaletteFor(form.colorId);
  return (
    <form onSubmit={onSubmit} style={{ display:"flex", flexDirection:"column", gap:8,
      padding:"10px 12px", borderRadius:7,
      border:`1.5px solid ${palette.color}44`,
      background:"var(--bg-subtle)" }}>
      <div style={{ display:"flex", gap:8, alignItems:"center" }}>
        <div style={{ width:14, height:14, borderRadius:3, flexShrink:0, background:palette.color }} />
        <input value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
          style={{ ...S.input, flex:1, fontSize:12, padding:"4px 8px" }}
          placeholder="Team name" />
        <LMCapacity value={form.capacity} onChange={v => setForm(f => ({ ...f, capacity: v }))} />
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
        <span style={{ fontSize:10, color:"var(--text-secondary)", fontWeight:600, flexShrink:0 }}>Color:</span>
        <LMColorPicker value={form.colorId} onChange={v => setForm(f => ({ ...f, colorId: v }))} />
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
        <span style={{ fontSize:10, color:"var(--text-secondary)", fontWeight:600, flexShrink:0, whiteSpace:"nowrap" }}>Jira Board ID:</span>
        <input value={form.jiraBoardId || ""} onChange={e => setForm(f => ({ ...f, jiraBoardId: e.target.value }))}
          style={{ ...S.input, flex:1, fontSize:12, padding:"4px 8px" }}
          placeholder="e.g. 5814 (optional)" />
      </div>
      <div style={{ display:"flex", gap:6 }}>
        <button type="submit" style={{ ...S.btnPrimary, fontSize:11, padding:"5px 12px" }}>{submitLabel}</button>
        <button type="button" onClick={onCancel} style={{ ...S.btnGhost, fontSize:11, padding:"5px 12px" }}>Cancel</button>
      </div>
    </form>
  );
}

// ─── LaneManagerModal ─────────────────────────────────────────────────────────
export function LaneManagerModal({ lanes, onSave, onClose }) {
  const [local,      setLocal]      = useState(() => lanes.map(l => ({ ...l })));
  const [editingId,  setEditingId]  = useState(null);
  const [editForm,   setEditForm]   = useState({ label:"", capacity:2, colorId:"green", jiraBoardId:"" });
  const [showAdd,    setShowAdd]    = useState(false);
  const [addForm,    setAddForm]    = useState({ label:"", capacity:2, colorId:"blue",  jiraBoardId:"" });
  const [error,      setError]      = useState("");

  const startEdit = (lane) => {
    const colorId = COLOR_PALETTE.find(c => c.color === lane.color)?.id || "blue";
    setEditingId(lane.id);
    setEditForm({ label: lane.label, capacity: lane.capacity, colorId, jiraBoardId: lane.jiraBoardId || "" });
    setShowAdd(false);
  };

  const saveEdit = (e) => {
    e.preventDefault();
    if (!editForm.label.trim()) return;
    const { id: _pid, ...paletteColors } = lmPaletteFor(editForm.colorId);
    setLocal(ll => ll.map(l => l.id === editingId
      ? { ...l, label: editForm.label.trim(), capacity: editForm.capacity, jiraBoardId: editForm.jiraBoardId.trim(), ...paletteColors }
      : l));
    setEditingId(null);
  };

  const handleAdd = (e) => {
    e.preventDefault();
    if (!addForm.label.trim()) { setError("Name is required"); return; }
    const { id: _pid, ...paletteColors } = lmPaletteFor(addForm.colorId);
    const newId = `lane_${Date.now()}`;
    setLocal(ll => [...ll, { id: newId, label: addForm.label.trim(), capacity: addForm.capacity, jiraBoardId: addForm.jiraBoardId.trim(), ...paletteColors }]);
    setAddForm({ label:"", capacity:2, colorId:"blue", jiraBoardId:"" });
    setShowAdd(false); setError("");
  };

  const handleDelete = (id) => {
    if (local.length <= 1) { setError("Must have at least one team"); return; }
    if (!window.confirm("Remove this team? Projects assigned to it will remain but show no team.")) return;
    setLocal(ll => ll.filter(l => l.id !== id));
  };

  return (
    <div style={S.overlay}>
      <div style={{ ...S.modal, width:460, maxHeight:"80vh", display:"flex", flexDirection:"column" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
          <div style={S.modalTitle}>Manage Teams</div>
          <span style={{ fontSize:11, color:"var(--text-muted)" }}>{local.length} team{local.length !== 1 ? "s" : ""}</span>
        </div>

        {error && (
          <div role="alert" style={{ fontSize:11, color:"#EF4444", fontWeight:700, marginBottom:10, padding:"6px 10px",
            background:"#FEF2F2", borderRadius:5, border:"1px solid #FECACA" }}>{error}</div>
        )}

        <div style={{ flex:1, overflowY:"auto", display:"flex", flexDirection:"column", gap:6 }}>
          {local.map(lane => (
            <div key={lane.id}>
              {editingId === lane.id ? (
                <LMInlineForm form={editForm} setForm={setEditForm} onSubmit={saveEdit}
                  onCancel={() => setEditingId(null)} submitLabel="Save" />
              ) : (
                <div style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 12px",
                  borderRadius:7, border:"1px solid var(--border)", background:"var(--bg-card)" }}>
                  <div style={{ width:14, height:14, borderRadius:3, flexShrink:0, background:lane.color }} />
                  <span style={{ flex:1, fontSize:12, fontWeight:700, color:"var(--text-primary)" }}>{lane.label}</span>
                  <span style={{ fontSize:10, color:"var(--text-muted)", whiteSpace:"nowrap" }}>
                    {lane.capacity} track{lane.capacity !== 1 ? "s" : ""}
                  </span>
                  {lane.jiraBoardId && (
                    <span style={{ fontSize:9, fontWeight:700, padding:"1px 6px", borderRadius:4,
                      background:"#DEEBFF", color:"#0052CC", border:"1px solid #4C9AFF", whiteSpace:"nowrap" }}>
                      Board {lane.jiraBoardId}
                    </span>
                  )}
                  <button onClick={() => { startEdit(lane); setError(""); }}
                    style={{ background:"transparent", border:"none", cursor:"pointer",
                      color:"var(--text-muted)", fontSize:11, padding:"2px 4px", borderRadius:3 }}
                    onMouseEnter={e => e.currentTarget.style.color="var(--text-primary)"}
                    onMouseLeave={e => e.currentTarget.style.color="var(--text-muted)"}>✏</button>
                  <DeleteButton onConfirm={() => handleDelete(lane.id)} compact />
                </div>
              )}
            </div>
          ))}

          {showAdd ? (
            <LMInlineForm form={addForm} setForm={v => { setAddForm(v); setError(""); }}
              onSubmit={handleAdd}
              onCancel={() => { setShowAdd(false); setAddForm({ label:"", capacity:2, colorId:"blue", jiraBoardId:"" }); setError(""); }}
              submitLabel="Add Team" />
          ) : (
            <button onClick={() => { setShowAdd(true); setEditingId(null); setError(""); }}
              style={{ padding:"9px 12px", fontSize:12, fontWeight:600, color:"var(--accent)",
                background:"transparent", border:"1px dashed var(--border)", borderRadius:7,
                cursor:"pointer", fontFamily:"inherit", textAlign:"left" }}
              onMouseEnter={e => { e.currentTarget.style.background="var(--bg-hover)"; e.currentTarget.style.borderColor="var(--accent)"; }}
              onMouseLeave={e => { e.currentTarget.style.background="transparent"; e.currentTarget.style.borderColor="var(--border)"; }}>
              + Add Team
            </button>
          )}
        </div>

        <div style={{ marginTop:16, display:"flex", gap:8, justifyContent:"flex-end" }}>
          <button onClick={() => { if (local.length === 0) { setError("Must have at least one team"); return; } onSave(local); onClose(); }}
            style={S.btnPrimary}>Save Changes</button>
          <button onClick={onClose} style={S.btnGhost}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── JiraStatusBadge ─────────────────────────────────────────────────────────
export function JiraStatusBadge({ status, statusCategory }) {
  const styles = {
    done:        { bg:"#E3FCEF", color:"#006644", border:"#79F2C0" },
    "in-progress":{ bg:"#DEEBFF", color:"#0052CC", border:"#4C9AFF" },
    undefined:   { bg:"#F4F5F7", color:"#42526E", border:"#C1C7D0" },
  };
  const s = styles[statusCategory] || styles.undefined;
  return (
    <span style={{ fontSize:10, fontWeight:700, padding:"2px 7px", borderRadius:4,
      background:s.bg, color:s.color, border:`1px solid ${s.border}`,
      display:"inline-flex", alignItems:"center", gap:4, lineHeight:1.6 }}>
      <span style={{ width:6, height:6, borderRadius:"50%", background:s.color, display:"inline-block", flexShrink:0 }} />
      {status}
    </span>
  );
}

// ─── ProjectModal ─────────────────────────────────────────────────────────────
export function ProjectModal({ initial, currentStatus, onSave, onClose, onDelete, lanes = LANES, scoringMethod = "score", allProjects = [] }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    name:"", lane: lanes[0]?.id ?? "pay", driver:"core", score:0, value:"", sprints:2, points:8, confluenceUrl:"", figmaUrl:"", aiInitiative:false, atRisk:false, legalRisk:false, appStoreRisk:false, imageUrl:"", notes:"", businessArea:"", initiativeStage:"", primarySurface:"", pmName:"", engLead:"", poName:"", jiraEpicKey:"",
    targetLaunchDate:"", confidenceLevel:"", externalDependency:"", partnerDependencies:"", successMetrics:"", eventTrackingKey:"", keyCode:"", certiniaKey:"",
    blockedBy: [],
    ...(initial || {}),
  });
  const [depSearch, setDepSearch] = useState("");
  const [status, setStatus] = useState(currentStatus || "backlog");
  const [nameError, setNameError] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showScoreCalc, setShowScoreCalc] = useState(false);
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); if (k === "name") setNameError(false); };

  const initialForm   = useRef(JSON.stringify(form));
  const initialStatus = useRef(status);
  const modalRef      = useRef(null);

  const isDirty = () =>
    JSON.stringify(form) !== initialForm.current || status !== initialStatus.current;

  const handleClose = () => {
    if (isDirty() && !window.confirm("You have unsaved changes. Discard them?")) return;
    onClose();
  };

  // Escape key + focus first input on open
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") handleClose(); };
    document.addEventListener("keydown", onKey);
    const first = modalRef.current?.querySelector("input, select, textarea, button");
    first?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const ext = file.name.split(".").pop();
      const path = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { error } = await supabase.storage.from("project-images").upload(path, file, { upsert: true });
      if (error) throw error;
      const { data } = supabase.storage.from("project-images").getPublicUrl(path);
      set("imageUrl", data.publicUrl);
    } catch (err) {
      alert("Image upload failed: " + err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
    <div style={S.overlay}>
      <div ref={modalRef} style={{ ...S.modal, width:820, padding:0, display:"flex", flexDirection:"column", maxHeight:"92vh", overflowY:"hidden" }}>

        {/* Header */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"16px 22px 14px", borderBottom:"1px solid var(--border)", flexShrink:0 }}>
          <span style={{ ...S.modalTitle, marginBottom:0 }}>{isEdit ? "Edit Project" : "Add Project to Backlog"}</span>
          <button onClick={handleClose} aria-label="Close modal" style={{ background:"none", border:"none", cursor:"pointer", fontSize:16, color:"var(--text-muted)", padding:0, lineHeight:1 }}>✕</button>
        </div>

        {/* Scrollable body */}
        <div style={{ flex:1, overflowY:"auto", padding:"16px 22px", display:"flex", flexDirection:"column", gap:12 }}>

          {/* Name – full width */}
          <div>
            <label htmlFor="pm-name" style={S.label}>Name <span aria-hidden="true" style={{ color:"var(--color-danger,#EF4444)" }}>*</span></label>
            <input id="pm-name" value={form.name} onChange={(e) => set("name", e.target.value)}
              aria-required="true" aria-invalid={nameError || undefined}
              style={{ ...S.input, borderColor: nameError ? "#EF4444" : undefined }} placeholder="Project name" />
            {nameError && <div role="alert" style={{ fontSize:10, color:"#EF4444", marginTop:3 }}>Name is required</div>}
          </div>

          {/* Two-column body */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20, alignItems:"start" }}>

            {/* ── Left: planning & priority ── */}
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>

              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                <div>
                  <label htmlFor="pm-lane" style={S.label}>Team</label>
                  <select id="pm-lane" value={form.lane} onChange={(e) => set("lane", e.target.value)} style={S.input}>
                    {lanes.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="pm-driver" style={S.label}>Driver</label>
                  <select id="pm-driver" value={form.driver} onChange={(e) => set("driver", e.target.value)} style={S.input}>
                    {Object.entries(DRIVERS).map(([k,d]) => <option key={k} value={k}>{d.label}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                <div>
                  <label htmlFor="pm-bizarea" style={S.label}>Business Area</label>
                  <select id="pm-bizarea" value={form.businessArea} onChange={(e) => set("businessArea", e.target.value)} style={S.input}>
                    <option value="">— None —</option>
                    {Object.entries(BUSINESS_AREAS).map(([k,a]) => <option key={k} value={k}>{a.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={S.label}>{scoringMethod === "rice" ? "RICE Score" : "Score"}</label>
                  <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                    {form.score > 0
                      ? (() => {
                          const st = scoringMethod === "rice"
                            ? { bg:"#EEF4FC", text:"#1A4ED8", border:"#A8C8F4" }
                            : SCORE_STYLE(form.score);
                          return <span style={{ ...S.pill, background:st.bg, color:st.text, border:`1px solid ${st.border}`, fontSize:13, fontWeight:800, padding:"4px 10px" }}>{form.score}</span>;
                        })()
                      : <span style={{ fontSize:11, color:"var(--text-muted)" }}>Not scored</span>}
                    <button type="button" onClick={() => setShowScoreCalc(true)}
                      style={{ ...S.btnGhost, whiteSpace:"nowrap", fontSize:11, padding:"7px 10px" }}>
                      {scoringMethod === "rice" ? "🔢 RICE" : "★ Score"}
                    </button>
                  </div>
                </div>
              </div>

              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                <div>
                  <label htmlFor="pm-stage" style={S.label}>Initiative Stage</label>
                  <select id="pm-stage" value={form.initiativeStage || ""} onChange={(e) => set("initiativeStage", e.target.value)} style={{
                    ...S.input,
                    ...(form.initiativeStage && INITIATIVE_STAGES[form.initiativeStage] ? {
                      background: INITIATIVE_STAGES[form.initiativeStage].bg,
                      color: INITIATIVE_STAGES[form.initiativeStage].color,
                      borderColor: INITIATIVE_STAGES[form.initiativeStage].border,
                      fontWeight: 700,
                    } : {}),
                  }}>
                    <option value="">— Not set —</option>
                    {Object.entries(INITIATIVE_STAGES).map(([k, s]) => <option key={k} value={k}>{s.icon} {s.label}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="pm-surface" style={S.label}>Primary Surface</label>
                  <select id="pm-surface" value={form.primarySurface || ""} onChange={(e) => set("primarySurface", e.target.value)} style={{
                    ...S.input,
                    ...(form.primarySurface && PRIMARY_SURFACES[form.primarySurface] ? {
                      background: PRIMARY_SURFACES[form.primarySurface].bg,
                      color: PRIMARY_SURFACES[form.primarySurface].color,
                      borderColor: PRIMARY_SURFACES[form.primarySurface].border,
                      fontWeight: 700,
                    } : {}),
                  }}>
                    <option value="">— Not set —</option>
                    {Object.entries(PRIMARY_SURFACES).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10 }}>
                <div>
                  <label htmlFor="pm-value" style={S.label}>$ Value</label>
                  <input id="pm-value" value={form.value} onChange={(e) => set("value", e.target.value)} style={S.input} placeholder="$100K" />
                </div>
                <div>
                  <label htmlFor="pm-sprints" style={S.label}>Sprints</label>
                  <input id="pm-sprints" type="number" min="1" max="10" value={form.sprints} onChange={(e) => set("sprints", parseInt(e.target.value)||1)} style={S.input} />
                </div>
                <div>
                  <label htmlFor="pm-points" style={S.label}>Points</label>
                  <input id="pm-points" type="number" min="1" max="20" value={form.points} onChange={(e) => set("points", parseInt(e.target.value)||1)} style={S.input} />
                </div>
              </div>

              {isEdit && (
                <div>
                  <label style={S.label}>Status</label>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:5 }}>
                    {Object.entries(STATUSES).map(([k, s]) => (
                      <button key={k} onClick={() => setStatus(k)} style={{
                        padding:"7px 4px", borderRadius:6, border:`1.5px solid ${status===k ? s.color : "var(--border)"}`,
                        background: status===k ? s.bg : "var(--bg-subtle)",
                        color: status===k ? s.color : "var(--text-muted)",
                        fontWeight:700, fontSize:10, cursor:"pointer", fontFamily:"inherit",
                        transition:"all 0.15s", whiteSpace:"nowrap",
                      }}>
                        {k === "completed" && "✓ "}
                        {k === "inprogress" && "▶ "}
                        {k === "notcommitted" && "? "}
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                <div>
                  <label htmlFor="pm-target" style={S.label}>Target Launch Date</label>
                  <input id="pm-target" type="date" value={form.targetLaunchDate || ""} onChange={(e) => set("targetLaunchDate", e.target.value)} style={S.input} />
                </div>
                <div>
                  <label htmlFor="pm-confidence" style={S.label}>Confidence Level</label>
                  <select id="pm-confidence" value={form.confidenceLevel || ""} onChange={(e) => set("confidenceLevel", e.target.value)} style={S.input}>
                    <option value="">— Not set —</option>
                    {Object.entries(CONFIDENCE_LEVELS).map(([k, c]) => <option key={k} value={k}>{c.label}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="pm-extdep" style={S.label}>External Dependency / Blocker</label>
                <input id="pm-extdep" value={form.externalDependency || ""} onChange={(e) => set("externalDependency", e.target.value)}
                  style={S.input} placeholder="e.g. Microblink Android SDK by June 15" />
              </div>

              <div>
                <label htmlFor="pm-partdep" style={S.label}>Partner Dependencies</label>
                <input id="pm-partdep" value={form.partnerDependencies || ""} onChange={(e) => set("partnerDependencies", e.target.value)}
                  style={S.input} placeholder="e.g. Way, insurance partner, car wash partner" />
              </div>

              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                {[
                  { key:"aiInitiative", icon:"🤖", label:"AI Initiative", onColor:"var(--accent)", onBg:"var(--card-ai-bg)", onBorder:"var(--accent-glow-strong)" },
                  { key:"atRisk",       icon:"🚩", label:"At Risk",       onColor:"#EF4444",        onBg:"#FEF2F2",           onBorder:"#FCA5A5" },
                  { key:"legalRisk",    icon:"⚖️", label:"Legal Risk",    onColor:"#7C3AED",        onBg:"#F5F3FF",           onBorder:"#C4B5FD" },
                  { key:"appStoreRisk", icon:"📱", label:"App Store Risk",onColor:"#0369A1",        onBg:"#EFF6FF",           onBorder:"#7DD3FC" },
                ].map(({ key, icon, label, onColor, onBg, onBorder }) => (
                  <div key={key} style={{ flex:"1 1 calc(50% - 4px)", display:"flex", alignItems:"center", gap:8, padding:"8px 10px",
                    background: form[key] ? onBg : "var(--bg-subtle)",
                    border:`1.5px solid ${form[key] ? onBorder : "var(--border)"}`,
                    borderRadius:7, cursor:"pointer", transition:"all 0.15s", userSelect:"none", minWidth:0 }}
                    onClick={() => set(key, !form[key])}>
                    <div style={{ width:16, height:16, borderRadius:4, border:`2px solid ${form[key] ? onColor : "var(--border)"}`,
                      background: form[key] ? onColor : "var(--bg-input)",
                      display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, transition:"all 0.15s" }}>
                      {form[key] && <span style={{ color:"#fff", fontSize:10, lineHeight:1, fontWeight:800 }}>✓</span>}
                    </div>
                    <div style={{ fontSize:10, fontWeight:800, color: form[key] ? onColor : "var(--text-secondary)", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{icon} {label}</div>
                  </div>
                ))}
              </div>

            </div>

            {/* ── Right: links, context & contacts ── */}
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>

              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                <div>
                  <label htmlFor="pm-confluence" style={S.label}>Confluence URL</label>
                  <div style={{ position:"relative" }}>
                    <span style={{ position:"absolute", left:9, top:"50%", transform:"translateY(-50%)", fontSize:13, pointerEvents:"none" }} aria-hidden="true">🔗</span>
                    <input id="pm-confluence" value={form.confluenceUrl} onChange={(e) => set("confluenceUrl", e.target.value)}
                      style={{ ...S.input, paddingLeft:28 }} placeholder="https://…atlassian.net/wiki/…" />
                  </div>
                </div>
                <div>
                  <label htmlFor="pm-figma" style={S.label}>Figma / Mock URL</label>
                  <div style={{ position:"relative" }}>
                    <span style={{ position:"absolute", left:9, top:"50%", transform:"translateY(-50%)", fontSize:13, pointerEvents:"none" }} aria-hidden="true">🎨</span>
                    <input id="pm-figma" value={form.figmaUrl} onChange={(e) => set("figmaUrl", e.target.value)}
                      style={{ ...S.input, paddingLeft:28 }} placeholder="https://figma.com/file/…" />
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="pm-keycode" style={S.label}>Key Code</label>
                <input id="pm-keycode" value={form.keyCode || ""}
                  onChange={(e) => set("keyCode", e.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 4))}
                  maxLength={4}
                  style={{ ...S.input, fontFamily:"ui-monospace,monospace", letterSpacing:"0.12em", fontWeight:700, width:120 }}
                  placeholder="A1B2" />
              </div>

              <div>
                <label htmlFor="pm-jira" style={S.label}>Jira Epic Key</label>
                <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                  <div style={{ position:"relative", flex:1 }}>
                    <span style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", fontSize:12, fontWeight:800, color:"#0052CC", pointerEvents:"none" }} aria-hidden="true">J</span>
                    <input id="pm-jira" value={form.jiraEpicKey || ""} onChange={(e) => set("jiraEpicKey", e.target.value.toUpperCase())}
                      style={{ ...S.input, paddingLeft:28 }} placeholder="PROJ-123" />
                  </div>
                  {form.jiraEpicKey && (
                    <a href={`https://pdisoftware.atlassian.net/browse/${form.jiraEpicKey}`} target="_blank" rel="noreferrer"
                      style={{ ...S.btnGhost, fontSize:11, padding:"7px 10px", whiteSpace:"nowrap", textDecoration:"none" }}>
                      Open ↗
                    </a>
                  )}
                </div>
                {form.jiraStatus && (
                  <div style={{ marginTop:5 }}>
                    <JiraStatusBadge status={form.jiraStatus} statusCategory={form.jiraStatusCategory} />
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="pm-certinia" style={S.label}>Certinia Project Key</label>
                <input id="pm-certinia" value={form.certiniaKey || ""} onChange={(e) => set("certiniaKey", e.target.value)}
                  style={S.input} placeholder="Billable project key for developers" />
              </div>

              <div>
                <label htmlFor="pm-notes" style={S.label}>Notes</label>
                <textarea id="pm-notes" value={form.notes || ""} onChange={(e) => set("notes", e.target.value)}
                  style={{ ...S.input, resize:"vertical", minHeight:72, lineHeight:1.5 }}
                  placeholder="Engineering notes, context, dependencies, risks..." />
              </div>

              {/* Blocked By */}
              {allProjects.filter((p) => p.id !== (initial?.id)).length > 0 && (
                <div>
                  <label style={S.label}>Blocked By (depends on)</label>
                  {(form.blockedBy || []).length > 0 && (
                    <div style={{ display:"flex", flexWrap:"wrap", gap:4, marginBottom:5 }}>
                      {(form.blockedBy || []).map((id) => {
                        const dep = allProjects.find((p) => p.id === id);
                        if (!dep) return null;
                        return (
                          <span key={id} style={{ fontSize:10, fontWeight:700, padding:"2px 7px", borderRadius:4,
                            background:"#FEF3C7", color:"#92400E", border:"1px solid #FDE68A",
                            display:"flex", alignItems:"center", gap:4 }}>
                            ⛓ {dep.name}
                            <button onClick={() => set("blockedBy", (form.blockedBy||[]).filter((x) => x !== id))}
                              style={{ background:"none", border:"none", cursor:"pointer", padding:0, color:"#92400E", lineHeight:1, fontSize:11 }}>✕</button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                  <div style={{ position:"relative" }}>
                    <input value={depSearch} onChange={(e) => setDepSearch(e.target.value)}
                      style={{ ...S.input, fontSize:11 }} placeholder="Search projects to add dependency…" />
                    {depSearch.trim() && (() => {
                      const q = depSearch.trim().toLowerCase();
                      const opts = allProjects.filter((p) =>
                        p.id !== (initial?.id) &&
                        !(form.blockedBy||[]).includes(p.id) &&
                        p.name.toLowerCase().includes(q)
                      ).slice(0, 6);
                      if (opts.length === 0) return null;
                      return (
                        <div style={{ position:"absolute", top:"calc(100% + 2px)", left:0, right:0, zIndex:50,
                          background:"var(--bg-card)", border:"1px solid var(--border)", borderRadius:7,
                          boxShadow:"0 8px 20px rgba(0,0,0,0.12)", overflow:"hidden" }}>
                          {opts.map((p) => (
                            <div key={p.id} style={{ padding:"7px 10px", cursor:"pointer", fontSize:11, fontWeight:600,
                              color:"var(--text-primary)", borderBottom:"1px solid var(--border)" }}
                              onMouseEnter={(e) => e.currentTarget.style.background="var(--bg-hover)"}
                              onMouseLeave={(e) => e.currentTarget.style.background="transparent"}
                              onClick={() => { set("blockedBy", [...(form.blockedBy||[]), p.id]); setDepSearch(""); }}>
                              {p.name}
                              <span style={{ fontSize:10, color:"var(--text-muted)", marginLeft:6 }}>
                                {lanes.find((l) => l.id === p.lane)?.label}
                              </span>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}

              <div>
                <label style={S.label}>Success Metrics (post-launch KPIs)</label>
                <textarea value={form.successMetrics || ""} onChange={(e) => set("successMetrics", e.target.value)}
                  style={{ ...S.input, resize:"vertical", minHeight:56, lineHeight:1.5 }}
                  placeholder="e.g. Sessions/MAU lift, rewards enrollment rate, CTA CTR" />
              </div>

              <div>
                <label style={S.label}>Analytics Event Key</label>
                <input value={form.eventTrackingKey || ""} onChange={(e) => set("eventTrackingKey", e.target.value)}
                  style={S.input} placeholder="e.g. Home_Screen_Redeem_Now_Button_Clicked" />
              </div>

              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10 }}>
                <div>
                  <label style={S.label}>Product Manager</label>
                  <input value={form.pmName || ""} onChange={(e) => set("pmName", e.target.value)} style={S.input} placeholder="Name" />
                </div>
                <div>
                  <label style={S.label}>Eng Lead</label>
                  <input value={form.engLead || ""} onChange={(e) => set("engLead", e.target.value)} style={S.input} placeholder="Name" />
                </div>
                <div>
                  <label style={S.label}>Product Owner</label>
                  <input value={form.poName || ""} onChange={(e) => set("poName", e.target.value)} style={S.input} placeholder="Name" />
                </div>
              </div>

              <div>
                <label style={S.label}>Project Image</label>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <label style={{ ...S.btnGhost, fontSize:11, cursor: uploading ? "not-allowed" : "pointer", display:"inline-flex", alignItems:"center", gap:5, opacity: uploading ? 0.7 : 1 }}>
                    {uploading ? <span className="spinner" /> : "🖼 "}
                    {uploading ? "Uploading…" : form.imageUrl ? "Replace Image" : "Upload Image"}
                    <input type="file" accept="image/*" style={{ display:"none" }} onChange={handleImageUpload} disabled={uploading} />
                  </label>
                  {form.imageUrl && (
                    <>
                      <img src={form.imageUrl} alt="" style={{ height:36, borderRadius:4, border:"1px solid var(--border)", objectFit:"cover", cursor:"pointer" }}
                        onClick={() => window.open(form.imageUrl, "_blank")}
                        onError={(e) => { e.target.style.display = "none"; }} />
                      <button onClick={() => set("imageUrl", "")} style={{ ...S.btnGhost, fontSize:10, padding:"3px 8px" }}>✕ Remove</button>
                    </>
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Footer – always visible */}
        <div style={{ padding:"14px 22px", borderTop:"1px solid var(--border)", background:"var(--bg-subtle)", display:"flex", gap:8, alignItems:"center", flexShrink:0, borderRadius:"0 0 14px 14px" }}>
          <button onClick={() => { if(form.name.trim()) { onSave(form, status); } else { setNameError(true); } }} style={S.btnPrimary}>
            {isEdit ? "Save Changes" : "Add to Backlog"}
          </button>
          <button onClick={handleClose} style={S.btnGhost}>Cancel</button>
          {isEdit && (
            <button onClick={() => { if(window.confirm("Delete this project?")) { onDelete(initial.id); onClose(); } }}
              style={{ ...S.btnGhost, color:"#EF4444", borderColor:"#FECACA", marginLeft:"auto" }}>
              🗑 Delete
            </button>
          )}
        </div>

      </div>
    </div>
    {showScoreCalc && scoringMethod === "score" && (
      <ScoreModal
        project={form}
        onClose={() => setShowScoreCalc(false)}
        onApply={(score, scoreComp) => {
          set("score", score);
          set("scoreComp", scoreComp);
          setShowScoreCalc(false);
        }}
      />
    )}
    {showScoreCalc && scoringMethod === "rice" && (
      <RiceModal
        project={form}
        onClose={() => setShowScoreCalc(false)}
        onApply={(score, riceComp) => {
          set("score", score);
          set("riceComp", riceComp);
          setShowScoreCalc(false);
        }}
      />
    )}
    </>
  );
}

// ─── ScoreModal ───────────────────────────────────────────────────────────────
const SCORE_FIELDS = [
  { key:"win",    label:"Competitive Win Rate", weight:25, color:"#0EA5E9", desc:"Does this improve our ability to win competitive deals or prevent churn?" },
  { key:"upsell", label:"Upsell / New Module",  weight:25, color:"#10B981", desc:"Does this create a net-new sellable module or meaningful upsell opportunity?" },
  { key:"eff",    label:"Efficiency / Margin",  weight:25, color:"#8B5CF6", desc:"Does this reduce cost, improve EBITDA margin, or drive operational efficiency?" },
  { key:"ease",   label:"Ease of Execution",    weight:15, color:"#F59E0B", desc:"How certain is delivery? Consider capacity, complexity, and dependency risk." },
  { key:"pay",    label:"Payback Period",        weight:10, color:"#EF4444", desc:"How quickly does this return value? Higher = faster payback." },
];

function calcScore(v) {
  return +((v.win||0)*0.25 + (v.upsell||0)*0.25 + (v.eff||0)*0.25 + (v.ease||0)*0.15 + (v.pay||0)*0.10).toFixed(1);
}

function StarRow({ val, onChange, color }) {
  return (
    <div style={{ display:"flex", gap:4 }}>
      {[1,2,3,4,5].map(n => (
        <span key={n} onClick={() => onChange(n)}
          style={{ fontSize:22, cursor:"pointer", color: n <= val ? color : "var(--border)", lineHeight:1, userSelect:"none" }}>
          {n <= val ? "★" : "☆"}
        </span>
      ))}
    </div>
  );
}

export function ScoreModal({ project, onApply, onClose }) {
  const [vals, setVals] = useState(project.scoreComp || { win:0, upsell:0, eff:0, ease:0, pay:0, note:"" });
  const score    = calcScore(vals);
  const sStyle   = SCORE_STYLE(score);
  const allRated = SCORE_FIELDS.every(f => vals[f.key] > 0);

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={{ ...S.modal, width:460 }} onClick={e => e.stopPropagation()}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
          <span style={S.modalTitle}>Weighted Score</span>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", fontSize:16, color:"var(--text-muted)", padding:0, lineHeight:1 }}>✕</button>
        </div>

        {SCORE_FIELDS.map(({ key, label, weight, color, desc }) => (
          <div key={key} style={{ marginBottom:14, paddingLeft:10, borderLeft:`3px solid ${color}`, background:`${color}08`, borderRadius:"0 6px 6px 0", padding:"8px 10px 8px 12px" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:4 }}>
              <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                <span style={{ fontSize:12, fontWeight:700, color }}>{label}</span>
                <span style={{ fontSize:10, fontWeight:700, padding:"1px 6px", borderRadius:10,
                  background:`${color}18`, color, border:`1px solid ${color}44` }}>{weight}%</span>
              </div>
              <StarRow val={vals[key]} onChange={v => setVals(prev => ({ ...prev, [key]: v }))} color={color} />
            </div>
            <p style={{ margin:0, fontSize:10, color:"var(--text-muted)", lineHeight:1.5 }}>{desc}</p>
          </div>
        ))}

        <div style={{ marginBottom:14 }}>
          <label htmlFor="score-note" style={S.label}>Justification / Notes</label>
          <textarea
            id="score-note"
            value={vals.note || ""}
            onChange={e => setVals(prev => ({ ...prev, note: e.target.value }))}
            placeholder="Why this score? Key context, trade-offs, or assumptions..."
            style={{ ...S.input, resize:"vertical", minHeight:72, lineHeight:1.5 }}
          />
        </div>

        <div style={{ borderTop:"1px solid var(--border)", paddingTop:14,
          display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div style={{ fontSize:10, color:"var(--text-muted)" }}>Weighted Score Preview</div>
          <span style={{ fontSize:22, fontWeight:900, color:sStyle.text, background:sStyle.bg,
            border:`2px solid ${sStyle.border}`, borderRadius:10, padding:"4px 16px",
            display:"inline-block", minWidth:64, textAlign:"center" }}>
            {allRated ? <>★ {score}</> : "—"}
          </span>
        </div>

        <div style={{ display:"flex", justifyContent:"flex-end", gap:8, marginTop:18 }}>
          <button onClick={onClose} style={S.btnGhost}>Cancel</button>
          <button onClick={() => onApply(score, vals)} style={{ ...S.btnPrimary, opacity: allRated ? 1 : 0.5, cursor: allRated ? "pointer" : "not-allowed" }}
            disabled={!allRated}>Save Score</button>
        </div>
      </div>
    </div>
  );
}

// ─── RiceModal ────────────────────────────────────────────────────────────────
const RICE_FIELDS = [
  { key:"reach",      label:"Reach",       color:"#0EA5E9",
    desc:"How many users or accounts does this impact per quarter? (1 = very few, 5 = entire user base)" },
  { key:"impact",     label:"Impact",      color:"#10B981",
    desc:"How significantly does this improve the experience per user? (1 = minimal, 5 = massive)" },
  { key:"confidence", label:"Confidence",  color:"#8B5CF6",
    desc:"How confident are you in the reach and impact estimates? (1 = low/gut feel, 5 = data-backed)" },
  { key:"effort",     label:"Effort",      color:"#EF4444",
    desc:"How much total effort is required? Rated INVERSELY — 5 stars = very low effort (quick win), 1 star = huge lift." },
];

function calcRice(v) {
  const r = v.reach      || 0;
  const i = v.impact     || 0;
  const c = v.confidence || 0;
  const e = v.effort     || 0;
  if (!r || !i || !c || !e) return 0;
  // (R × I × C) / E, normalized: max = (5×5×5)/1 = 125 → scale to 100
  return Math.min(100, Math.round((r * i * c) / (6 - e) / 25 * 100));
}

export function RiceModal({ project, onApply, onClose }) {
  const [vals, setVals] = useState(project.riceComp || { reach:0, impact:0, confidence:0, effort:0, note:"" });
  const score    = calcRice(vals);
  const allRated = RICE_FIELDS.every(f => vals[f.key] > 0);
  const rStyle   = { bg:"#EEF4FC", text:"#1A4ED8", border:"#A8C8F4" };

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={{ ...S.modal, width:460 }} onClick={e => e.stopPropagation()}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
          <span style={S.modalTitle}>RICE Score</span>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", fontSize:16, color:"var(--text-muted)", padding:0, lineHeight:1 }}>✕</button>
        </div>

        {RICE_FIELDS.map(({ key, label, color, desc }) => (
          <div key={key} style={{ marginBottom:14, borderLeft:`3px solid ${color}`, background:`${color}08`, borderRadius:"0 6px 6px 0", padding:"8px 10px 8px 12px" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:4 }}>
              <span style={{ fontSize:12, fontWeight:700, color }}>{label}</span>
              <StarRow val={vals[key]} onChange={v => setVals(prev => ({ ...prev, [key]: v }))} color={color} />
            </div>
            <p style={{ margin:0, fontSize:10, color:"var(--text-muted)", lineHeight:1.5 }}>{desc}</p>
          </div>
        ))}

        <div style={{ marginBottom:14 }}>
          <label htmlFor="rice-note" style={S.label}>Justification / Notes</label>
          <textarea
            id="rice-note"
            value={vals.note || ""}
            onChange={e => setVals(prev => ({ ...prev, note: e.target.value }))}
            placeholder="Why this score? Key assumptions, data sources, risks..."
            style={{ ...S.input, resize:"vertical", minHeight:72, lineHeight:1.5 }}
          />
        </div>

        <div style={{ borderTop:"1px solid var(--border)", paddingTop:14,
          display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div style={{ fontSize:10, color:"var(--text-muted)" }}>RICE Score Preview (0–100)</div>
          <span style={{ fontSize:22, fontWeight:900, color:rStyle.text, background:rStyle.bg,
            border:`2px solid ${rStyle.border}`, borderRadius:10, padding:"4px 16px",
            display:"inline-block", minWidth:64, textAlign:"center" }}>
            {allRated ? score : "—"}
          </span>
        </div>

        <div style={{ display:"flex", justifyContent:"flex-end", gap:8, marginTop:18 }}>
          <button onClick={onClose} style={S.btnGhost}>Cancel</button>
          <button onClick={() => onApply(score, vals)} style={{ ...S.btnPrimary, opacity: allRated ? 1 : 0.5, cursor: allRated ? "pointer" : "not-allowed" }}
            disabled={!allRated}>Save RICE Score</button>
        </div>
      </div>
    </div>
  );
}
